



import { Camera, Coffee, LogOut, MonitorUp, Timer } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  startBreakApi,
  endBreakApi,
  getMyAttendanceApi,
  getTodayDailyWorkLogApi,
} from "../app/api";
import CameraModal from "./CameraModal";
import DailyWorkLogModal from "./DailyWorkLogModal";
import { getDateInputValue, getTodayDateInputValue } from "../utils/dateFormat";

function UptaskLogoMark() {
  return (
    <div className="uptask-logo-3d relative h-11 w-11 shrink-0 rounded-2xl bg-gradient-to-br from-violet-500 via-indigo-500 to-cyan-400 shadow-[0_14px_28px_rgba(34,211,238,0.24),inset_0_1px_0_rgba(255,255,255,0.42)] ring-1 ring-white/25">
      <div className="absolute inset-[4px] rounded-xl bg-white/16 shadow-[inset_0_1px_8px_rgba(255,255,255,0.28)]" />
      <div className="absolute inset-0 rounded-2xl bg-[linear-gradient(145deg,rgba(255,255,255,0.34),transparent_42%,rgba(15,23,42,0.22))]" />
      <div className="absolute left-3.5 top-3.5 h-3 w-3 rounded-full border-2 border-white/90 shadow-[0_0_10px_rgba(255,255,255,0.45)]" />
      <div className="absolute left-4 top-[26px] h-2.5 w-2.5 rounded-sm border-l-[3px] border-b-[3px] border-white rotate-[-45deg]" />
      <div className="uptask-logo-orbit absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-cyan-100 shadow-[0_0_12px_rgba(207,250,254,0.95)]" />
    </div>
  );
}

function VisiontrekBrandHero() {
  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-0 z-0 hidden h-full items-center justify-center lg:flex"
      aria-hidden="true"
    >
      <style>
        {`
          @keyframes visiontrekShine {
            0% { transform: translateX(-130%) skewX(-18deg); opacity: 0; }
            18%, 56% { opacity: 0.55; }
            100% { transform: translateX(185%) skewX(-18deg); opacity: 0; }
          }
        `}
      </style>

      <div className="relative h-[136px] w-[560px]">
        <div className="absolute left-1/2 top-[104px] h-8 w-[380px] -translate-x-1/2 rounded-full bg-cyan-950/55 blur-xl" />
        <div
          className="absolute inset-x-0 top-7 mx-auto h-[92px] w-[500px] rounded-[30px] border border-white/12 bg-[#120a32]/45 shadow-[0_30px_80px_rgba(4,10,33,0.40),inset_0_1px_0_rgba(255,255,255,0.20)] backdrop-blur-sm"
        >
          <div className="absolute inset-0 overflow-hidden rounded-[30px]">
            <div
              className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-transparent via-white/55 to-transparent"
              style={{ animation: "visiontrekShine 5.8s ease-in-out infinite" }}
            />
          </div>
          <span className="absolute left-8 top-7 h-12 w-12 rounded-2xl border border-cyan-100/22 bg-gradient-to-br from-cyan-300/28 to-violet-400/20 shadow-[0_18px_44px_rgba(34,211,238,0.16)]" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div
              className="relative text-[40px] font-black leading-none text-white"
              style={{
                textShadow:
                  "0 1px 0 rgba(186,230,253,0.55), 0 4px 0 rgba(34,21,77,0.55), 0 18px 34px rgba(0,0,0,0.40)",
              }}
            >
              <span className="absolute left-[3px] top-[4px] -z-10 text-cyan-400/24">
                Visiontrek
              </span>
              <span className="bg-gradient-to-r from-white via-cyan-100 to-violet-100 bg-clip-text text-transparent">
                Visiontrek
              </span>
            </div>
            <div className="mt-1 text-[18px] font-bold leading-none text-cyan-100/88 drop-shadow-[0_10px_18px_rgba(34,211,238,0.18)]">
              Communication
            </div>
          </div>
          <span className="absolute bottom-4 right-8 h-1.5 w-24 rounded-full bg-gradient-to-r from-cyan-200 to-violet-300 shadow-[0_0_22px_rgba(34,211,238,0.35)]" />
        </div>
      </div>
    </div>
  );
}

function RotatingClock({ seconds }) {
  const secondRotation = (seconds % 60) * 6;      // fast hand
  const minuteRotation = (seconds % 3600) * 0.1; // slow hand
  const tickOrigin =
    typeof window !== "undefined" && window.innerWidth < 768
      ? "center 21px"
      : "center 27px";

  return (
    <div className="relative flex h-[58px] w-[58px] items-center justify-center md:h-[90px] md:w-[90px]">

      {/* SHADOW PLATE (STATIC) */}
      <div className="absolute bottom-0 h-[10px] w-[42px] rounded-full bg-black/30 blur-md md:h-[14px] md:w-[55px]" />

      {/* OUTER CLOCK BODY (STATIC) */}
      <div
        className="
          relative h-[48px] w-[48px] rounded-full md:h-[60px] md:w-[60px]
          bg-gradient-to-br from-[#c7d2fe] to-[#8b9cff]
          shadow-[0_10px_25px_rgba(0,0,0,0.35)]
          flex items-center justify-center
        "
      >
        {/* OUTER RING */}
        <div className="absolute inset-[2px] rounded-full border border-white/40" />

        {/* ================= ROTATING INNER PART ================= */}
        <div
          className="absolute inset-0 flex items-center justify-center
                     transition-transform duration-1000 linear"
          style={{ transform: `rotate(${secondRotation}deg)` }}
        >
          {/* TICK MARKS */}
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute top-[3px] h-[5px] w-[2px] bg-[#4b5cff] md:h-[6px]"
              style={{
                transform: `rotate(${i * 30}deg)`,
                transformOrigin: tickOrigin,
             
              }}
            />
          ))}
        </div>

        {/* INNER FACE */}
        <div className="absolute inset-[7px] rounded-full bg-gradient-to-br from-[#eef2ff] to-[#c7d2fe]" />

        {/* SMALL HAND (SLOW) */}
        <div
          className="absolute bottom-1/2 h-[11px] w-[2px] origin-bottom rounded-full bg-[#4b5cff] md:h-[14px]
                     transition-transform duration-1000 linear"
          style={{ transform: `rotate(${minuteRotation}deg)` }}
        />

        {/* BIG HAND (FAST) */}
        <div
          className="absolute bottom-1/2 h-[16px] w-[3px] origin-bottom rounded-full bg-[#ef4444] md:h-[20px]
                     transition-transform duration-1000 linear"
          style={{ transform: `rotate(${secondRotation}deg)` }}
        />

        {/* CENTER PIN */}
        <div className="absolute w-2.5 h-2.5 bg-[#1e1b4b] rounded-full z-10" />

        {/* TOP BUTTON (STATIC) */}
        <div className="absolute -top-2 w-4 h-2 bg-[#6b7cff] rounded-md shadow-md" />
      </div>
    </div>
  );
}

function FloatingWorkTimer({
  workSeconds,
  breakSeconds,
  status,
  formatSeconds,
  onOpenDesktopTimer,
}) {
  const TIMER_POSITION_KEY = "uptaskFloatingTimerPosition";
  const isOnBreak = status === "BREAK";
  const requiredSeconds = 9 * 60 * 60;
  const progress = Math.min((workSeconds / requiredSeconds) * 100, 100);
  const timerRef = useRef(null);
  const dragRef = useRef({
    dragging: false,
    pointerId: null,
    offsetX: 0,
    offsetY: 0,
  });

  const getSafePosition = (nextPosition) => {
    const margin = window.innerWidth < 640 ? 10 : 16;
    const width = Math.min(window.innerWidth - margin * 2, window.innerWidth < 640 ? 286 : 320);
    const height = timerRef.current?.offsetHeight || 132;
    const maxX = Math.max(margin, window.innerWidth - width - margin);
    const maxY = Math.max(margin, window.innerHeight - height - margin);

    return {
      x: Math.min(Math.max(nextPosition.x, margin), maxX),
      y: Math.min(Math.max(nextPosition.y, margin), maxY),
    };
  };

  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") return { x: 16, y: 16 };

    try {
      const saved = JSON.parse(localStorage.getItem(TIMER_POSITION_KEY) || "null");
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
        return saved;
      }
    } catch {}

    return {
      x: window.innerWidth < 640 ? 10 : 16,
      y: Math.max(window.innerWidth < 640 ? 10 : 16, window.innerHeight - 150),
    };
  });

  useEffect(() => {
    const clampPosition = () => {
      setPosition((current) => {
        const safe = getSafePosition(current);
        localStorage.setItem(TIMER_POSITION_KEY, JSON.stringify(safe));
        return safe;
      });
    };

    clampPosition();
    window.addEventListener("resize", clampPosition);
    window.addEventListener("orientationchange", clampPosition);

    return () => {
      window.removeEventListener("resize", clampPosition);
      window.removeEventListener("orientationchange", clampPosition);
    };
  }, []);

  const startDrag = (event) => {
    if (event.target.closest("[data-no-drag='true']")) return;

    const rect = timerRef.current?.getBoundingClientRect();
    if (!rect) return;

    dragRef.current = {
      dragging: true,
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };

    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const moveDrag = (event) => {
    if (!dragRef.current.dragging || dragRef.current.pointerId !== event.pointerId) return;

    const safe = getSafePosition({
      x: event.clientX - dragRef.current.offsetX,
      y: event.clientY - dragRef.current.offsetY,
    });

    setPosition(safe);
    localStorage.setItem(TIMER_POSITION_KEY, JSON.stringify(safe));
  };

  const stopDrag = (event) => {
    if (!dragRef.current.dragging || dragRef.current.pointerId !== event.pointerId) return;

    dragRef.current.dragging = false;
    dragRef.current.pointerId = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    localStorage.setItem(TIMER_POSITION_KEY, JSON.stringify(position));
  };

  return createPortal(
    <div
      ref={timerRef}
      className="fixed w-[min(calc(100vw-18px),246px)] select-none sm:w-[274px]"
      style={{
        left: position.x,
        top: position.y,
        zIndex: 2147483647,
        WebkitTransform: "translate3d(0,0,0)",
        transform: "translate3d(0,0,0)",
      }}
    >
      <div
        className="relative overflow-hidden rounded-2xl border border-white/15 bg-[#12082F]/95 p-2.5 text-white shadow-[0_18px_44px_rgba(0,0,0,0.48),inset_0_1px_0_rgba(255,255,255,0.18),inset_0_-18px_34px_rgba(0,0,0,0.2)] backdrop-blur-2xl sm:p-3"
        style={{ WebkitBackdropFilter: "blur(24px)", touchAction: "none" }}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
      >
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.16),transparent_32%),radial-gradient(circle_at_16%_12%,rgba(34,211,238,0.32),transparent_34%),radial-gradient(circle_at_92%_12%,rgba(168,85,247,0.28),transparent_38%)]" />
        <div className="pointer-events-none absolute -left-10 top-1/2 h-24 w-24 -translate-y-1/2 rounded-full bg-cyan-300/15 blur-2xl" />
        <div className="relative flex items-center gap-2.5 cursor-grab active:cursor-grabbing">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-cyan-200/10 text-cyan-100 ring-1 ring-cyan-100/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_10px_22px_rgba(34,211,238,0.12)] sm:h-10 sm:w-10">
            <Timer className={`h-4 w-4 sm:h-[18px] sm:w-[18px] ${isOnBreak ? "text-amber-200" : "text-cyan-100"}`} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-0.5 flex items-center justify-between gap-2">
              <p className="text-[7px] font-bold uppercase tracking-[0.2em] text-cyan-100/70 sm:text-[8px]">
                {isOnBreak ? "Break running" : "Work running"}
              </p>
              <span className={`h-1.5 w-1.5 rounded-full ${isOnBreak ? "bg-amber-300" : "bg-emerald-300"} shadow-[0_0_13px_currentColor]`} />
            </div>

            <div className="flex items-end justify-between gap-2">
              <div>
                <p className="text-lg font-black leading-none tracking-normal drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)] sm:text-[22px]">
                  {formatSeconds(workSeconds)}
                </p>
                <p className="mt-0.5 hidden text-[9px] text-white/45 min-[360px]:block">Visible until punch out</p>
              </div>
              <div className="text-right">
                <p className="text-[7px] uppercase tracking-wider text-white/35 sm:text-[8px]">Break</p>
                <p className="text-[11px] font-semibold text-white/75 sm:text-xs">{formatSeconds(breakSeconds)}</p>
              </div>
            </div>

            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/10 shadow-[inset_0_1px_2px_rgba(0,0,0,0.35)]">
              <div
                className={`h-full rounded-full ${isOnBreak ? "bg-amber-300 shadow-[0_0_10px_rgba(252,211,77,0.55)]" : "bg-cyan-300 shadow-[0_0_10px_rgba(103,232,249,0.55)]"}`}
                style={{ width: `${progress}%` }}
              />
            </div>

            <button
              type="button"
              onClick={onOpenDesktopTimer}
              data-no-drag="true"
              className="mt-1.5 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-cyan-100/20 bg-cyan-300/10 px-2 py-1.5 text-[10px] font-semibold text-cyan-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_8px_18px_rgba(0,0,0,0.18)] transition hover:-translate-y-0.5 hover:bg-cyan-300/18"
            >
              <MonitorUp className="h-3 w-3" />
              Open desktop timer
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}



export default function HeroHeader() {


  const [locationError, setLocationError] = useState(false);
  const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
  const dailyWorkExemptEmails = new Set(["nitin.jha@visiontrek.in"]);
  const shouldSkipDailyWorkForm =
    [1, 2, 3].includes(Number(loggedInUser?.role_id)) ||
    dailyWorkExemptEmails.has(String(loggedInUser?.email || "").toLowerCase());

  const isMobile = window.innerWidth < 768;
  const REQUIRED_WORK_SECONDS = 9 * 60 * 60;
  const WORK_SUMMARY_REMINDER_SECONDS = REQUIRED_WORK_SECONDS - 10 * 60;
  const IDLE_LIMIT_MINUTES = 20;
  const IDLE_LIMIT_SECONDS = IDLE_LIMIT_MINUTES * 60;
  const IDLE_CONFIRM_SECONDS = 60;
  const dailyWorkPromptUserKey =
    loggedInUser?.id || loggedInUser?.user_id || loggedInUser?.email || "guest";
  const getDailyWorkPromptKey = (mode, date = getTodayDateInputValue()) =>
    `uptaskDailyWorkPrompt:${dailyWorkPromptUserKey}:${date}:${mode}`;
  const hasDailyWorkPromptBeenShown = (mode, date = getTodayDateInputValue()) =>
    localStorage.getItem(getDailyWorkPromptKey(mode, date)) === "1";
  const markDailyWorkPromptShown = (mode, date = getTodayDateInputValue()) => {
    localStorage.setItem(getDailyWorkPromptKey(mode, date), "1");
  };

  const [attendance, setAttendance] = useState({ status: "NOT_PUNCHED" });
  const [dailyWorkModalMode, setDailyWorkModalMode] = useState(null);
  const [workSeconds, setWorkSeconds] = useState(0);
  const [breakSeconds, setBreakSeconds] = useState(0);
  const [totalBreakSeconds, setTotalBreakSeconds] = useState(0);
  const [showPunchOutConfirm, setShowPunchOutConfirm] = useState(false);
  const [idlePromptOpen, setIdlePromptOpen] = useState(false);
  const [idleConfirmSeconds, setIdleConfirmSeconds] = useState(IDLE_CONFIRM_SECONDS);
  const [idleBreakLoading, setIdleBreakLoading] = useState(false);




  const workHours = String(Math.floor(workSeconds / 3600)).padStart(2, "0");
  const workMinutes = String(
    Math.floor((workSeconds % 3600) / 60)
  ).padStart(2, "0");
  const workSecs = String(workSeconds % 60).padStart(2, "0");
  

  const breakHours = String(Math.floor(breakSeconds / 3600)).padStart(2, "0");
  const breakMinutes = String(
    Math.floor((breakSeconds % 3600) / 60)
  ).padStart(2, "0");
  const breakSecs = String(breakSeconds % 60).padStart(2, "0");
  




  const [openCamera, setOpenCamera] = useState(null);
  const [location, setLocation] = useState(null);
  const desktopTimerWindowRef = useRef(null);
  const idleDetectorRef = useRef(null);
  const idleAbortControllerRef = useRef(null);
  const idlePromptOpenRef = useRef(false);
  const idleActionRunningRef = useRef(false);
  const dailyWorkPromptShownRef = useRef(
    hasDailyWorkPromptBeenShown("morning")
  );
  const workSummaryPromptShownRef = useRef(
    hasDailyWorkPromptBeenShown("punchOut")
  );

  /* ================= DERIVED STATES ================= */
  const isPunchedIn =
    attendance.status === "IN" || attendance.status === "BREAK";
  const isOnBreak = attendance.status === "BREAK";

  /* ================= HELPERS ================= */
  const fsec = (s = 0) =>
    new Date(Math.max(s, 0) * 1000).toISOString().substr(11, 8);

  const getDesktopTimerHtml = ({ status, workSeconds, breakSeconds }) => {
    const isOnBreak = status === "BREAK";
    const progress = Math.min((workSeconds / REQUIRED_WORK_SECONDS) * 100, 100);
    const statusLabel = isOnBreak ? "Break running" : "Work running";
    const accent = isOnBreak ? "#fbbf24" : "#22d3ee";

    return `
      <!doctype html>
      <html>
        <head>
          <title>Uptask Work Timer</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            * { box-sizing: border-box; }
            html, body {
              width: 100%;
              height: 100%;
              margin: 0;
              overflow: hidden;
              font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
              background: #12082f;
              color: white;
            }
            body {
              display: grid;
              place-items: center;
              background:
                radial-gradient(circle at 18% 15%, rgba(34,211,238,0.28), transparent 34%),
                radial-gradient(circle at 90% 10%, rgba(168,85,247,0.24), transparent 36%),
                #12082f;
            }
            .card {
              width: calc(100% - 18px);
              height: calc(100% - 18px);
              border: 1px solid rgba(255,255,255,0.16);
              border-radius: 20px;
              padding: 16px;
              background: rgba(18,8,47,0.96);
              box-shadow: inset 0 1px 0 rgba(255,255,255,0.10);
            }
            .top {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
            }
            .brand {
              display: flex;
              align-items: center;
              gap: 10px;
              min-width: 0;
            }
            .icon {
              width: 40px;
              height: 40px;
              border-radius: 14px;
              display: grid;
              place-items: center;
              background: rgba(34,211,238,0.13);
              border: 1px solid rgba(34,211,238,0.22);
              color: ${accent};
            }
            .label {
              margin: 0;
              font-size: 10px;
              font-weight: 800;
              letter-spacing: 0.22em;
              text-transform: uppercase;
              color: rgba(207,250,254,0.72);
            }
            .status-dot {
              width: 10px;
              height: 10px;
              border-radius: 999px;
              background: ${accent};
              box-shadow: 0 0 22px ${accent};
            }
            .time {
              margin: 14px 0 0;
              font-size: 34px;
              line-height: 1;
              font-weight: 900;
              letter-spacing: -0.03em;
            }
            .meta {
              display: flex;
              align-items: end;
              justify-content: space-between;
              margin-top: 8px;
              color: rgba(255,255,255,0.5);
              font-size: 12px;
            }
            .break strong {
              display: block;
              color: rgba(255,255,255,0.82);
              font-size: 14px;
            }
            .progress {
              height: 7px;
              margin-top: 13px;
              overflow: hidden;
              border-radius: 999px;
              background: rgba(255,255,255,0.12);
            }
            .bar {
              height: 100%;
              width: ${progress}%;
              border-radius: inherit;
              background: ${accent};
            }
          </style>
        </head>
        <body>
          <div class="card">
            <div class="top">
              <div class="brand">
                <div class="icon">◷</div>
                <div>
                  <p class="label">${statusLabel}</p>
                  <div style="font-size:12px;color:rgba(255,255,255,0.42);margin-top:3px;">UPTASK timer</div>
                </div>
              </div>
              <span class="status-dot"></span>
            </div>
            <div class="time">${fsec(workSeconds)}</div>
            <div class="meta">
              <span>Visible until punch out</span>
              <span class="break">Break <strong>${fsec(breakSeconds)}</strong></span>
            </div>
            <div class="progress"><div class="bar"></div></div>
          </div>
        </body>
      </html>
    `;
  };

  const renderDesktopTimer = () => {
    const timerWindow = desktopTimerWindowRef.current;
    if (!timerWindow || timerWindow.closed) {
      desktopTimerWindowRef.current = null;
      return;
    }

    timerWindow.document.open();
    timerWindow.document.write(
      getDesktopTimerHtml({
        status: attendance.status,
        workSeconds,
        breakSeconds,
      })
    );
    timerWindow.document.close();
  };

  const openDesktopTimer = async ({ silent = false } = {}) => {
    try {
      if (desktopTimerWindowRef.current && !desktopTimerWindowRef.current.closed) {
        desktopTimerWindowRef.current.focus();
        renderDesktopTimer();
        return;
      }

      let timerWindow = null;
      if ("documentPictureInPicture" in window) {
        timerWindow = await window.documentPictureInPicture.requestWindow({
          width: 340,
          height: 175,
        });
      } else {
        const left = 20;
        const top = Math.max(20, window.screen.availHeight - 230);
        timerWindow = window.open(
          "",
          "uptask-work-timer",
          `popup=yes,width=360,height=190,left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=yes,alwaysRaised=yes`
        );
      }

      if (!timerWindow) throw new Error("Timer window blocked");

      desktopTimerWindowRef.current = timerWindow;
      localStorage.setItem("uptaskDesktopTimerEnabled", "true");
      renderDesktopTimer();
    } catch {
      if (!silent) {
        alert("Please allow popups or click the desktop timer button again.");
      }
    }
  };

  // const getLocation = () =>
  //   new Promise((resolve, reject) => {
  //     if (!navigator.geolocation) return reject("No location support");
  //     navigator.geolocation.getCurrentPosition(
  //       (pos) =>
  //         resolve({
  //           lat: pos.coords.latitude,
  //           lng: pos.coords.longitude,
  //         }),
  //       reject
  //     );
  //   });

  const getLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      return reject("Geolocation not supported");
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        reject(err);
      },
      {
        enableHighAccuracy: true,
        timeout: 20000,   // iOS needs slightly more time
        maximumAge: 0,
      }
    );
  });
  /* ================= LOAD ATTENDANCE (REFRESH SAFE) ================= */
  useEffect(() => {
    loadAttendance();
  }, []);

  const checkDailyWorkPrompt = async (attendanceRow, status, restoredWork) => {
    if (shouldSkipDailyWorkForm || !attendanceRow?.punch_in) return;

    const today = getTodayDateInputValue();
    const attendanceDate = attendanceRow.attendance_date
      ? getDateInputValue(attendanceRow.attendance_date)
      : today;

    if (attendanceDate !== today) return;

    try {
      const logRes = await getTodayDailyWorkLogApi(today);
      const log = logRes?.data || null;

      if (
        !log?.morning_description &&
        !dailyWorkPromptShownRef.current &&
        !hasDailyWorkPromptBeenShown("morning", today)
      ) {
        dailyWorkPromptShownRef.current = true;
        markDailyWorkPromptShown("morning", today);
        setDailyWorkModalMode("morning");
        return;
      }

      const shouldAskForSummary =
        (status === "IN" || status === "BREAK" || attendanceRow.punch_out) &&
        restoredWork >= WORK_SUMMARY_REMINDER_SECONDS;

      if (
        shouldAskForSummary &&
        !log?.punch_out_description &&
        !workSummaryPromptShownRef.current &&
        !hasDailyWorkPromptBeenShown("punchOut", today)
      ) {
        workSummaryPromptShownRef.current = true;
        markDailyWorkPromptShown("punchOut", today);
        setDailyWorkModalMode("punchOut");
      }
    } catch {
      // Keep attendance usable even if the daily-work prompt cannot load.
    }
  };

  const loadAttendance = async () => {
    const res = await getMyAttendanceApi();
    if (!res?.success || !Array.isArray(res.data)) return resetAttendance();

    const today = getTodayDateInputValue();
    const a = res.data.find(
      (i) =>
        getDateInputValue(i.attendance_date) === today
    );

    if (!a) return resetAttendance();

    const fullDate = a.attendance_date.split("T")[0];
    const join = (d, t) => (t ? `${d}T${t}` : null);

    const punchInFull = join(fullDate, a.punch_in);
    const breakStartFull = join(fullDate, a.break_start);

    let status = "NOT_PUNCHED";
    if (a.punch_in && !a.punch_out) {
      status = a.break_start && !a.break_end ? "BREAK" : "IN";
    }
    if (a.punch_out) status = "OUT";

    /* ===== RESTORE WORK TIME ===== */
    let restoredWork = (a.total_minutes || 0) * 60;

    if ((status === "IN" || status === "BREAK") && punchInFull) {
      restoredWork += Math.floor(
        (Date.now() - new Date(punchInFull)) / 1000
      );

      if (status === "BREAK" && breakStartFull) {
        restoredWork -= Math.floor(
          (Date.now() - new Date(breakStartFull)) / 1000
        );
      }
    }

    /* ===== RESTORE BREAK TIME ===== */
    let restoredBreak = (a.break_minutes || 0) * 60;
    if (status === "BREAK" && breakStartFull) {
      restoredBreak += Math.floor(
        (Date.now() - new Date(breakStartFull)) / 1000
      );
    }

    setAttendance({ status });
    setWorkSeconds(Math.max(restoredWork, 0));
    setBreakSeconds(restoredBreak);
    setTotalBreakSeconds(restoredBreak);

    checkDailyWorkPrompt(a, status, Math.max(restoredWork, 0));
  };

  const resetAttendance = () => {
    setAttendance({ status: "NOT_PUNCHED" });
    setWorkSeconds(0);
    setBreakSeconds(0);
    setTotalBreakSeconds(0);
    dailyWorkPromptShownRef.current = false;
    workSummaryPromptShownRef.current = false;
  };

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") {
        loadAttendance();
      }
    };

    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    const interval = setInterval(refreshIfVisible, 60 * 1000);

    return () => {
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
      clearInterval(interval);
    };
  }, []);

  const requestSystemIdlePermission = async () => {
    if (!("IdleDetector" in window)) return "unsupported";
    if (!window.IdleDetector.requestPermission) return "granted";

    try {
      return await window.IdleDetector.requestPermission();
    } catch {
      return "unavailable";
    }
  };

  const startIdleBreak = async () => {
    if (attendance.status !== "IN" || idleActionRunningRef.current) return;

    idleActionRunningRef.current = true;
    setIdleBreakLoading(true);

    try {
      await startBreakApi();
      setAttendance({ status: "BREAK" });
      setIdlePromptOpen(false);
      idlePromptOpenRef.current = false;
    } finally {
      setIdleBreakLoading(false);
      idleActionRunningRef.current = false;
    }
  };

  const continueAfterIdle = () => {
    setIdleConfirmSeconds(IDLE_CONFIRM_SECONDS);
    setIdlePromptOpen(false);
    idlePromptOpenRef.current = false;
  };

  /* ================= LIVE TIMERS ================= */
  useEffect(() => {
    if (!attendance?.status) return;

    const interval = setInterval(() => {
      if (attendance.status === "IN" || attendance.status === "BREAK") {
        setWorkSeconds((s) => s + 1);
      }
      if (attendance.status === "BREAK") {
        setBreakSeconds((s) => s + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [attendance.status]);

  useEffect(() => {
    const today = getTodayDateInputValue();

    if (
      shouldSkipDailyWorkForm ||
      workSummaryPromptShownRef.current ||
      hasDailyWorkPromptBeenShown("punchOut", today) ||
      !(attendance.status === "IN" || attendance.status === "BREAK") ||
      workSeconds < WORK_SUMMARY_REMINDER_SECONDS
    ) {
      return;
    }

    let cancelled = false;
    workSummaryPromptShownRef.current = true;
    markDailyWorkPromptShown("punchOut", today);

    getTodayDailyWorkLogApi()
      .then((res) => {
        if (cancelled) return;
        if (!res?.data?.punch_out_description) {
          setDailyWorkModalMode("punchOut");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDailyWorkModalMode("punchOut");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attendance.status, shouldSkipDailyWorkForm, workSeconds]);

  useEffect(() => {
    idlePromptOpenRef.current = idlePromptOpen;
  }, [idlePromptOpen]);

  useEffect(() => {
    if (attendance.status !== "IN") {
      idleAbortControllerRef.current?.abort();
      idleAbortControllerRef.current = null;
      idleDetectorRef.current = null;
      setIdlePromptOpen(false);
      idlePromptOpenRef.current = false;
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();
    idleAbortControllerRef.current?.abort();
    idleAbortControllerRef.current = abortController;

    const startSystemIdleWatcher = async () => {
      const permission = await requestSystemIdlePermission();
      if (cancelled || permission !== "granted") return;

      const detector = new window.IdleDetector();
      idleDetectorRef.current = detector;

      detector.addEventListener("change", () => {
        if (idlePromptOpenRef.current) return;

        const systemIsIdle =
          detector.userState === "idle" || detector.screenState === "locked";

        if (systemIsIdle) {
          setIdlePromptOpen(true);
          setIdleConfirmSeconds(IDLE_CONFIRM_SECONDS);
        }
      });

      try {
        await detector.start({
          threshold: IDLE_LIMIT_SECONDS * 1000,
          signal: abortController.signal,
        });
      } catch {
        idleDetectorRef.current = null;
      }
    };

    startSystemIdleWatcher();

    return () => {
      cancelled = true;
      abortController.abort();
      if (idleAbortControllerRef.current === abortController) {
        idleAbortControllerRef.current = null;
      }
      idleDetectorRef.current = null;
    };
  }, [attendance.status]);

  useEffect(() => {
    if (!idlePromptOpen || attendance.status !== "IN") return;

    const countdown = setInterval(() => {
      setIdleConfirmSeconds((seconds) => {
        if (seconds <= 1) {
          clearInterval(countdown);
          startIdleBreak();
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [idlePromptOpen, attendance.status]);

  useEffect(() => {
    if (!isPunchedIn) {
      if (desktopTimerWindowRef.current && !desktopTimerWindowRef.current.closed) {
        desktopTimerWindowRef.current.close();
      }
      desktopTimerWindowRef.current = null;
      localStorage.removeItem("uptaskDesktopTimerEnabled");
      return;
    }

    renderDesktopTimer();
  }, [isPunchedIn, attendance.status, workSeconds, breakSeconds]);

  useEffect(() => {
    if (
      isPunchedIn &&
      localStorage.getItem("uptaskDesktopTimerEnabled") === "true" &&
      !desktopTimerWindowRef.current
    ) {
      openDesktopTimer({ silent: true });
    }
  }, [isPunchedIn]);

  /* ================= ACTIONS ================= */
  // const handlePunchIn = async () => {
  //   const loc = await getLocation();
  //   setLocation(loc);
  //   setOpenCamera("IN");
  // };
  const handlePunchIn = async () => {
    try {
      await requestSystemIdlePermission();
      const loc = await getLocation();
      setLocation(loc);
      setOpenCamera("IN");
    } catch {
      setLocation(null);
      setOpenCamera("IN");
    }
  };

  // const handlePunchOut = async () => {
  //   const loc = await getLocation();
  //   setLocation(loc);
  //   setOpenCamera("OUT");
  // };


  // const handlePunchOut = async () => {
  //   try {
  //     const loc = await getLocation();
  //     setLocation(loc);
  //     setOpenCamera("OUT");
  //   } catch {
  //     setLocationError(true);
  //   }
  // };



  const handlePunchOut = async () => {
    if (workSeconds < REQUIRED_WORK_SECONDS) {
      setShowPunchOutConfirm(true);
      return;
    }
  
    try {
      const loc = await getLocation();
      setLocation(loc);
      setOpenCamera("OUT");
    } catch {
      setLocation(null);
      setOpenCamera("OUT");
    }
  };



  const handleBreak = async () => {
    if (attendance.status === "BREAK") {
      const res = await endBreakApi();
      const total = (res?.data?.total_break_minutes || 0) * 60;
      setTotalBreakSeconds(total);
      setBreakSeconds(total);
      setAttendance({ status: "IN" });
    } else {
      await startBreakApi();
      setAttendance({ status: "BREAK" });
    }
  };

  /* ================= UI (UNCHANGED) ================= */
  return (
    <>
      {openCamera && (
        <CameraModal
          mode={openCamera}
          location={location}
          onClose={() => setOpenCamera(null)}
          loadAttendance={loadAttendance}
          onPunchSuccess={(completedMode, punchData = {}) => {
            if (completedMode === "IN") {
              const punchDate = punchData?.attendance_date || getTodayDateInputValue();
              const punchInAt = punchData?.punch_in
                ? new Date(`${punchDate}T${punchData.punch_in}`)
                : null;
              const elapsedSeconds =
                punchInAt && !Number.isNaN(punchInAt.getTime())
                  ? Math.max(1, Math.floor((Date.now() - punchInAt.getTime()) / 1000))
                  : 1;

              setAttendance({ status: "IN" });
              setWorkSeconds(elapsedSeconds);
              setBreakSeconds(0);
              setTotalBreakSeconds(0);
            } else {
              setAttendance({ status: "OUT" });
              setWorkSeconds((punchData?.total_minutes || 0) * 60);
              setBreakSeconds(0);
            }

            if (shouldSkipDailyWorkForm) return;
            const mode = completedMode === "IN" ? "morning" : "punchOut";
            const today = getTodayDateInputValue();
            if (hasDailyWorkPromptBeenShown(mode, today)) return;
            if (mode === "morning") {
              dailyWorkPromptShownRef.current = true;
            } else {
              workSummaryPromptShownRef.current = true;
            }
            markDailyWorkPromptShown(mode, today);
            setDailyWorkModalMode(mode);
          }}
        />
      )}

      <DailyWorkLogModal
        open={!!dailyWorkModalMode}
        mode={dailyWorkModalMode}
        onClose={() => setDailyWorkModalMode(null)}
      />

      {isPunchedIn && (
        <FloatingWorkTimer
          workSeconds={workSeconds}
          breakSeconds={breakSeconds}
          status={attendance.status}
          formatSeconds={fsec}
          onOpenDesktopTimer={openDesktopTimer}
        />
      )}

      {idlePromptOpen && (
        <div className="fixed inset-0 z-[2147483646] flex items-center justify-center bg-[#05010f]/72 px-4 backdrop-blur-md">
          <div className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-white/15 bg-[#140a35]/95 p-6 text-white shadow-[0_30px_100px_rgba(0,0,0,0.58)]">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_18%_10%,rgba(34,211,238,0.22),transparent_34%),radial-gradient(circle_at_92%_18%,rgba(251,191,36,0.18),transparent_32%)]" />

            <div className="relative">
              <div className="mb-5 flex items-start gap-4">
                <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-amber-300/14 text-amber-100 ring-1 ring-amber-200/25">
                  <Timer className="h-7 w-7" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.28em] text-cyan-100/60">
                    No activity detected
                  </p>
                  <h3 className="mt-2 text-2xl font-black tracking-tight">
                    Are you still working?
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-white/58">
                    Your system has been idle or locked for {IDLE_LIMIT_MINUTES} minutes. If there is no response, Uptask will start your break automatically.
                  </p>
                </div>
              </div>

              <div className="mb-5 rounded-2xl border border-white/10 bg-white/7 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/50">Auto break in</span>
                  <span className="font-mono text-lg font-black text-amber-200">
                    00:{String(idleConfirmSeconds).padStart(2, "0")}
                  </span>
                </div>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full rounded-full bg-amber-300 transition-all"
                    style={{ width: `${(idleConfirmSeconds / IDLE_CONFIRM_SECONDS) * 100}%` }}
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={continueAfterIdle}
                  className="rounded-2xl bg-cyan-300 px-4 py-3 text-sm font-black text-[#07111f] shadow-[0_16px_35px_rgba(34,211,238,0.22)] transition hover:bg-cyan-200"
                >
                  Still working
                </button>
                <button
                  type="button"
                  onClick={startIdleBreak}
                  disabled={idleBreakLoading}
                  className="rounded-2xl border border-amber-200/25 bg-amber-300/14 px-4 py-3 text-sm font-black text-amber-100 transition hover:bg-amber-300/20 disabled:opacity-60"
                >
                  {idleBreakLoading ? "Starting..." : "Start break"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    continueAfterIdle();
                    handlePunchOut();
                  }}
                  className="rounded-2xl border border-red-300/25 bg-red-400/14 px-4 py-3 text-sm font-black text-red-100 transition hover:bg-red-400/20"
                >
                  Punch out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="w-[calc(100%-32px)] max-w-[1680px] mx-auto mt-2 px-3 sm:w-[calc(100%-48px)] sm:px-6">
        <div className="relative overflow-hidden rounded-[22px] bg-[#190b3f] px-4 pb-7 pt-3 text-white shadow-[0_28px_90px_rgba(23,9,61,0.62)] border border-white/10 sm:rounded-[26px] sm:px-6 sm:pb-16">
          <VisiontrekBrandHero />
          <div className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(circle_at_18%_12%,rgba(34,211,238,0.20),transparent_32%),radial-gradient(circle_at_83%_18%,rgba(168,85,247,0.22),transparent_36%),linear-gradient(135deg,rgba(255,255,255,0.10),transparent_38%,rgba(15,23,42,0.18))]" />
          <div className="pointer-events-none absolute inset-x-6 bottom-3 z-0 h-px bg-gradient-to-r from-transparent via-cyan-200/55 to-transparent" />




        <div className="relative z-10 flex flex-col justify-between gap-2 sm:flex-row sm:gap-4">
   
        <div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:gap-4">
  {/* LEFT: TITLE */}
  <Link
    to="/"
    className="group flex min-w-0 items-center gap-2 rounded-2xl px-1 py-1 transition hover:bg-white/8 focus:outline-none focus:ring-2 focus:ring-cyan-200/45 sm:gap-3 sm:px-2 sm:py-2 md:mt-[4px]"
    aria-label="Go to dashboard home"
  >
    <UptaskLogoMark />
    <div className="min-w-0">
      <h2 className="text-base font-semibold leading-tight transition group-hover:text-cyan-100 sm:text-xl">UPTASK</h2>
      <p className="max-w-[130px] truncate text-xs opacity-70 transition group-hover:opacity-90 sm:max-w-none sm:text-sm">
        Your everyday companion !!
      </p>
    </div>
  </Link>

  {/* RIGHT: CLOCK */}
  {/* <RotatingClock seconds={workSeconds} /> */}
  <div className="shrink-0 md:hidden">
  <RotatingClock seconds={workSeconds} />
</div>


</div>




          <div className="grid w-full grid-cols-[1fr_1fr_auto] items-center gap-1.5 rounded-2xl border border-white/10 bg-black/15 p-1.5 text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur sm:flex sm:w-auto sm:flex-wrap sm:gap-2 sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
            <div className="rounded-xl bg-white/8 px-2 py-1 text-left sm:bg-transparent sm:px-0 sm:py-0 sm:text-right">
              <p className="text-[10px] font-semibold uppercase tracking-wide opacity-60 sm:text-sm sm:normal-case sm:tracking-normal">Work</p>
              <p className="whitespace-nowrap text-[10px] font-semibold sm:text-sm">{fsec(workSeconds)}</p>
            </div> 
             
            <div className="rounded-xl bg-white/8 px-2 py-1 text-left sm:bg-transparent sm:px-0 sm:py-0 sm:text-right">
              <p className="text-[10px] font-semibold uppercase tracking-wide opacity-60 sm:text-sm sm:normal-case sm:tracking-normal">Break</p>
              <p className="whitespace-nowrap text-[10px] font-semibold sm:text-sm">{fsec(breakSeconds)}</p>
            </div>




<button
  onClick={isPunchedIn ? handlePunchOut : handlePunchIn}
  className={`flex h-9 shrink-0 items-center justify-center gap-1 rounded-xl px-2 text-[10px] font-semibold leading-none shadow-[0_8px_18px_rgba(0,0,0,0.2)] transition sm:h-auto sm:rounded sm:px-3 sm:py-1.5 sm:text-sm
    ${
      isPunchedIn
        ? "bg-red-500 hover:bg-red-600"
        : "bg-green-500 hover:bg-green-600"
    }
  `}
>
  {isPunchedIn ? (
    <>
      <LogOut size={11} /> <span className="whitespace-nowrap">Punch Out</span>
    </>
  ) : (
    <>
      <Camera size={11} /> <span className="whitespace-nowrap">Punch In</span>
    </>
  )}
</button>


            <button
              onClick={handleBreak}
              disabled={!isPunchedIn}
              // className={`md:px-3 md:py-1.5 px-1.5 py-1 rounded flex items-center gap-1 text-[11px] md:text-sm transition
              className={`hidden md:flex md:px-3 md:py-1.5 px-1.5 py-1 rounded items-center gap-1 text-[11px] md:text-sm transition
                ${
                  !isPunchedIn
                    ? "bg-gray-500 cursor-not-allowed"
                    : isOnBreak
                    ? "bg-blue-500 hover:scale-105"
                    : "bg-yellow-400 text-black hover:scale-105"
                }
              `}
            >
              <Coffee size={14} /> {isOnBreak ? "End Break" : "Break"}
            </button>

            
          </div>
        </div>
      </div>
      </div>


{/* ================= PUNCH OUT CONFIRM MODAL ================= */}
{showPunchOutConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
        <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-sm">
    
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Punch Out Confirmation
          </h3>
    
          <p className="mb-4 text-gray-600 text-sm leading-relaxed">
            You <strong>haven’t completed the required 9 working hours yet</strong>.
            <br />
 Do you really want to punch out?
           
          </p>
    
          <div className="flex justify-end gap-3">
            {/* CANCEL */}
            <button
              onClick={() => setShowPunchOutConfirm(false)}
              className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </button>
    
            {/* OK */}
            <button
             onClick={async () => {
              setShowPunchOutConfirm(false);
              try {
                const loc = await getLocation();
                setLocation(loc);
                setOpenCamera("OUT");
              } catch {
                setLocation(null);
                setOpenCamera("OUT");
                }
                }}


              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              OK, Punch Out
            </button>
          </div>
        </div>
      </div>
         )}

      {locationError && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-xl text-center max-w-sm">
      <h3 className="text-lg font-semibold mb-2">
        Location Unavailable
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Location could not be detected. You can still punch in or punch out.
      </p>
      <button
        onClick={() => setLocationError(false)}
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
      >
        OK
      </button>
    </div>
  </div>
)}
    </>
  );
}






// import { Camera, Coffee, LogOut } from "lucide-react";
// import { useEffect, useState } from "react";
// import {
//   startBreakApi,
//   endBreakApi,
//   getMyAttendanceApi,
// } from "../app/api";
// import CameraModal from "./CameraModal";

// function RotatingClock({ seconds }) {
//   const secondRotation = (seconds % 60) * 6;      // fast hand
//   const minuteRotation = (seconds % 3600) * 0.1; // slow hand

//   return (
//     <div className="relative w-[90px] h-[90px] flex items-center justify-center">

//       {/* SHADOW PLATE (STATIC) */}
//       <div className="absolute bottom-0 w-[55px] h-[14px] bg-black/30 blur-md rounded-full" />

//       {/* OUTER CLOCK BODY (STATIC) */}
//       <div
//         className="
//           relative md:w-[60px] md:h-[60px] w-[60px] h-[60px] rounded-full
//           bg-gradient-to-br from-[#c7d2fe] to-[#8b9cff]
//           shadow-[0_10px_25px_rgba(0,0,0,0.35)]
//           flex items-center justify-center
//         "
//       >
//         {/* OUTER RING */}
//         <div className="absolute inset-[2px] rounded-full border border-white/40" />

//         {/* ================= ROTATING INNER PART ================= */}
//         <div
//           className="absolute inset-0 flex items-center justify-center
//                      transition-transform duration-1000 linear"
//           style={{ transform: `rotate(${secondRotation}deg)` }}
//         >
//           {/* TICK MARKS */}
//           {[...Array(12)].map((_, i) => (
//             <div
//               key={i}
//               className="absolute md:w-[2px] md:h-[6px] w-[2px] h-[6px] bg-[#4b5cff] top-[3px]"
//               style={{
//                 transform: `rotate(${i * 30}deg)`,
//                 transformOrigin: "center 27px",
             
//               }}
//             />
//           ))}
//         </div>

//         {/* INNER FACE */}
//         <div className="absolute inset-[7px] rounded-full bg-gradient-to-br from-[#eef2ff] to-[#c7d2fe]" />

//         {/* SMALL HAND (SLOW) */}
//         <div
//           className="absolute w-[2px] h-[14px] bg-[#4b5cff] origin-bottom bottom-1/2 rounded-full
//                      transition-transform duration-1000 linear"
//           style={{ transform: `rotate(${minuteRotation}deg)` }}
//         />

//         {/* BIG HAND (FAST) */}
//         <div
//           className="absolute w-[3px] h-[20px] bg-[#ef4444] origin-bottom bottom-1/2 rounded-full
//                      transition-transform duration-1000 linear"
//           style={{ transform: `rotate(${secondRotation}deg)` }}
//         />

//         {/* CENTER PIN */}
//         <div className="absolute w-2.5 h-2.5 bg-[#1e1b4b] rounded-full z-10" />

//         {/* TOP BUTTON (STATIC) */}
//         <div className="absolute -top-2 w-4 h-2 bg-[#6b7cff] rounded-md shadow-md" />
//       </div>
//     </div>
//   );
// }



// export default function HeroHeader() {

//   const isMobile = window.innerWidth < 768;
//   const REQUIRED_WORK_SECONDS = 9 * 60 * 60;

//   const [attendance, setAttendance] = useState({ status: "NOT_PUNCHED" });
//   const [workSeconds, setWorkSeconds] = useState(0);
//   const [breakSeconds, setBreakSeconds] = useState(0);
//   const [totalBreakSeconds, setTotalBreakSeconds] = useState(0);





//   const workHours = String(Math.floor(workSeconds / 3600)).padStart(2, "0");
//   const workMinutes = String(
//     Math.floor((workSeconds % 3600) / 60)
//   ).padStart(2, "0");
//   const workSecs = String(workSeconds % 60).padStart(2, "0");
  

//   const breakHours = String(Math.floor(breakSeconds / 3600)).padStart(2, "0");
//   const breakMinutes = String(
//     Math.floor((breakSeconds % 3600) / 60)
//   ).padStart(2, "0");
//   const breakSecs = String(breakSeconds % 60).padStart(2, "0");
  




//   const [openCamera, setOpenCamera] = useState(null);
//   const [location, setLocation] = useState(null);

//   /* ================= DERIVED STATES ================= */
//   const isPunchedIn =
//     attendance.status === "IN" || attendance.status === "BREAK";
//   const isOnBreak = attendance.status === "BREAK";

//   /* ================= HELPERS ================= */
//   const fsec = (s = 0) =>
//     new Date(Math.max(s, 0) * 1000).toISOString().substr(11, 8);

//   // const getLocation = () =>
//   //   new Promise((resolve, reject) => {
//   //     if (!navigator.geolocation) return reject("No location support");
//   //     navigator.geolocation.getCurrentPosition(
//   //       (pos) =>
//   //         resolve({
//   //           lat: pos.coords.latitude,
//   //           lng: pos.coords.longitude,
//   //         }),
//   //       reject
//   //     );
//   //   });

//   const getLocation = () =>
//   new Promise((resolve, reject) => {
//     if (!navigator.geolocation) {
//       return reject("Geolocation not supported");
//     }

//     navigator.permissions
//       ?.query({ name: "geolocation" })
//       .then((permissionStatus) => {
//         if (permissionStatus.state === "denied") {
//           return reject("LOCATION_DENIED");
//         }

//         navigator.geolocation.getCurrentPosition(
//           (pos) => {
//             resolve({
//               lat: pos.coords.latitude,
//               lng: pos.coords.longitude,
//             });
//           },
//           (err) => {
//             reject(err.code);
//           },
//           {
//             enableHighAccuracy: true,
//             timeout: 15000,
//             maximumAge: 0,
//           }
//         );
//       })
//       .catch(() => {
//         // fallback if permissions API not supported
//         navigator.geolocation.getCurrentPosition(
//           (pos) => {
//             resolve({
//               lat: pos.coords.latitude,
//               lng: pos.coords.longitude,
//             });
//           },
//           reject,
//           {
//             enableHighAccuracy: true,
//             timeout: 15000,
//             maximumAge: 0,
//           }
//         );
//       });
//   });

//   /* ================= LOAD ATTENDANCE (REFRESH SAFE) ================= */
//   useEffect(() => {
//     loadAttendance();
//   }, []);

//   const loadAttendance = async () => {
//     const res = await getMyAttendanceApi();
//     if (!res?.success || !Array.isArray(res.data)) return resetAttendance();

//     const today = new Date().toLocaleDateString("en-CA");
//     const a = res.data.find(
//       (i) =>
//         new Date(i.attendance_date).toLocaleDateString("en-CA") === today
//     );

//     if (!a) return resetAttendance();

//     const fullDate = a.attendance_date.split("T")[0];
//     const join = (d, t) => (t ? `${d}T${t}` : null);

//     const punchInFull = join(fullDate, a.punch_in);
//     const breakStartFull = join(fullDate, a.break_start);

//     let status = "NOT_PUNCHED";
//     if (a.punch_in && !a.punch_out) {
//       status = a.break_start && !a.break_end ? "BREAK" : "IN";
//     }
//     if (a.punch_out) status = "OUT";

//     /* ===== RESTORE WORK TIME ===== */
//     let restoredWork = (a.total_minutes || 0) * 60;

//     if ((status === "IN" || status === "BREAK") && punchInFull) {
//       restoredWork += Math.floor(
//         (Date.now() - new Date(punchInFull)) / 1000
//       );

//       if (status === "BREAK" && breakStartFull) {
//         restoredWork -= Math.floor(
//           (Date.now() - new Date(breakStartFull)) / 1000
//         );
//       }
//     }

//     /* ===== RESTORE BREAK TIME ===== */
//     let restoredBreak = (a.break_minutes || 0) * 60;
//     if (status === "BREAK" && breakStartFull) {
//       restoredBreak += Math.floor(
//         (Date.now() - new Date(breakStartFull)) / 1000
//       );
//     }

//     setAttendance({ status });
//     setWorkSeconds(Math.max(restoredWork, 0));
//     setBreakSeconds(restoredBreak);
//     setTotalBreakSeconds(restoredBreak);
//   };

//   const resetAttendance = () => {
//     setAttendance({ status: "NOT_PUNCHED" });
//     setWorkSeconds(0);
//     setBreakSeconds(0);
//     setTotalBreakSeconds(0);
//   };

//   /* ================= LIVE TIMERS ================= */
//   useEffect(() => {
//     if (!attendance?.status) return;

//     const interval = setInterval(() => {
//       if (attendance.status === "IN" || attendance.status === "BREAK") {
//         setWorkSeconds((s) => s + 1);
//       }
//       if (attendance.status === "BREAK") {
//         setBreakSeconds((s) => s + 1);
//       }
//     }, 1000);

//     return () => clearInterval(interval);
//   }, [attendance.status]);

//   /* ================= ACTIONS ================= */
//   const handlePunchIn = async () => {
//     const loc = await getLocation();
//     setLocation(loc);
//     setOpenCamera("IN");
//   };

//   const handlePunchOut = async () => {
//     const loc = await getLocation();
//     setLocation(loc);
//     setOpenCamera("OUT");
//   };

//   const handleBreak = async () => {
//     if (attendance.status === "BREAK") {
//       const res = await endBreakApi();
//       const total = (res?.data?.total_break_minutes || 0) * 60;
//       setTotalBreakSeconds(total);
//       setBreakSeconds(total);
//       setAttendance({ status: "IN" });
//     } else {
//       await startBreakApi();
//       setAttendance({ status: "BREAK" });
//     }
//   };

//   /* ================= UI (UNCHANGED) ================= */
//   return (
//     <>
//       {openCamera && (
//         <CameraModal
//           mode={openCamera}
//           location={location}
//           onClose={() => setOpenCamera(null)}
//           loadAttendance={loadAttendance}
//         />
//       )}

//       <div className="max-w-[1400px] mx-auto mt-2 px-4 sm:px-6">
//         <div className="rounded-2xl px-6 pt-3 pb-16 bg-gradient-to-r from-[#2D1C6A] via-[#291857] to-[#352074] text-white">




//         <div className="flex flex-col  sm:flex-row justify-between gap-4">
   
//         <div className="flex items-center justify-between w-full sm:w-auto gap-4">
//   {/* LEFT: TITLE */}
//   <div>
//     <h2 className="text-xl font-semibold mt-[-24px] md:mt-[4px]">UPTASK</h2>
//     <p className="text-sm opacity-70">
//       Your everyday companion !!
//     </p>
//   </div>

//   {/* RIGHT: CLOCK */}
//   {/* <RotatingClock seconds={workSeconds} /> */}
//   <div className="md:hidden">
//   <RotatingClock seconds={workSeconds} />
// </div>


// </div>




//           <div className="flex flex-wrap items-center gap-2 text-sm">
//             <div className="text-right">
//               <p className="opacity-60 text-[14px] md:text-sm">Work</p>
//               <p className="text-[10px] md:text-sm">{fsec(workSeconds)}</p>
//             </div> 
             
//             <div className="text-right">
//               <p className="opacity-60 text-[14px] md:text-sm">Break</p>
//               <p className="text-[10px] md:text-sm">{fsec(breakSeconds)}</p>
//             </div>




// <button
//   onClick={isPunchedIn ? handlePunchOut : handlePunchIn}
//   className={`md:px-3 md:py-1.5 px-1 py-1 rounded flex text-[11px] md:text-sm items-center gap-1 transition
//     ${
//       isPunchedIn
//         ? "bg-red-500 hover:bg-red-600"
//         : "bg-green-500 hover:bg-green-600"
//     }
//   `}
// >
//   {isPunchedIn ? (
//     <>
//       <LogOut size={10} /> Punch Out
//     </>
//   ) : (
//     <>
//       <Camera size={10} /> Punch In
//     </>
//   )}
// </button>


//             <button
//               onClick={handleBreak}
//               disabled={!isPunchedIn}
//               className={`md:px-3 md:py-1.5 px-1.5 py-1 rounded flex items-center gap-1 text-[11px] md:text-sm transition
//                 ${
//                   !isPunchedIn
//                     ? "bg-gray-500 cursor-not-allowed"
//                     : isOnBreak
//                     ? "bg-blue-500 hover:scale-105"
//                     : "bg-yellow-400 text-black hover:scale-105"
//                 }
//               `}
//             >
//               <Coffee size={14} /> {isOnBreak ? "End Break" : "Break"}
//             </button>

            
//           </div>
//         </div>
//       </div>
//       </div>
//     </>
//   );
// }




